import { useCallback, useEffect } from 'react';
import { useTranslation } from '@/i18n';
import { FacilityFlowCoreModulesPlanningEntitiesDriverRole as DriverRoleEnum } from '@/api/generated/models';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockTimeClockEventDto as TimeClockEvent } from '@/api/generated/models';
import { useVehicleStore } from '@/store/useVehicleStore';

const ROLE_KEY: Record<number, string> = {
  [DriverRoleEnum.NUMBER_1]: 'driver',
  [DriverRoleEnum.NUMBER_2]: 'coDriver',
  [DriverRoleEnum.NUMBER_3]: 'none',
};

/**
 * Turns the vehicle choice of a clock-in/out event into "W 123 AB (Fahrer)" — or null for
 * events without one. Loads all vehicles (incl. deactivated) once, so old days still resolve.
 */
export function useDescribeVehicle(): (event: TimeClockEvent) => string | null {
  const { t } = useTranslation('vehicles');
  const vehicles = useVehicleStore((s) => s.vehicles);
  const fetchVehicles = useVehicleStore((s) => s.fetchVehicles);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  return useCallback(
    (event: TimeClockEvent) => {
      const roleKey = event.driverRole ? ROLE_KEY[event.driverRole] : undefined;
      if (!roleKey) return null;
      const role = t(`driverRole.${roleKey}`);
      const plate = event.vehicleId
        ? vehicles.find((v) => v.id === event.vehicleId)?.licensePlate
        : null;
      return plate ? t('eventLabel', { plate, role }) : role;
    },
    [t, vehicles],
  );
}
